import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import SignatureManager from "../../components/SignatureManager";
import ReviewManager from "../../components/ReviewManager";
import CompanyAddress from "../../components/CompanyAddress";
import logo from "../../images/logo.png";
import "../../styles/Form.css";

function EmergencyContact() {
  const navigate = useNavigate();
  const { id: paramId } = useParams();

  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // ✅ Start users editable by default; we'll lock it only if signed.
  const [isReadOnly, setIsReadOnly] = useState(isAdmin ? false : false);
  const [isSigned, setIsSigned] = useState(false);

  const [form, setForm] = useState({
    ec1_name: "",
    ec1_relationship: "",
    ec1_address: "",
    ec1_phone: "",
    ec1_email: "",
    ec2_name: "",
    ec2_relationship: "",
    ec2_address: "",
    ec2_phone: "",
    ec2_email: "",
  });

  // Small helper: normalize task-status -> "completed"?
  const isEmergencyTaskCompleted = (status) => {
    if (!status) return false;
    const TASK_ID = "3"; // string key
    const taskStatus = status[TASK_ID] ?? status.emergency_contact;
    return taskStatus === "review" || taskStatus === "completed";
  };

  useEffect(() => {
    if (!userId || !BASE_URL) return;

    const token = localStorage.getItem("token");
    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const fetchEverything = async () => {
      try {
        // 1) Always check task status first
        const statusRes = await axios.get(
          `${BASE_URL}/api/usertasks/status/${userId}`,
          auth
        );
        const signed = isEmergencyTaskCompleted(statusRes.data?.status);
        setIsSigned(signed);
        if (!isAdmin) setIsReadOnly(signed); // lock only if truly completed

        // 2) Fetch form data (for admins can be a different user)
        const fetchUrl =
          isAdmin && paramId
            ? `${BASE_URL}/api/emergencycontact/emergency-contact/${userId}`
            : `${BASE_URL}/api/emergencycontact/emergency-contact`;

        const formRes = await axios.get(fetchUrl, auth);
        if (formRes.data) setForm(formRes.data);
      } catch (err) {
        // ✅ Use err.response, not err.res
        const status = err?.response?.status;

        // If the form record doesn't exist (404), allow the user to fill it out.
        if (status === 404 && !isAdmin) {
          setIsReadOnly(false);
          return;
        }

        console.error("Fetch error:", err?.response?.data || err?.message);
      }
    };

    fetchEverything();
    // deps: when user/admin context or BASE_URL changes
  }, [BASE_URL, isAdmin, userId, paramId]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BASE_URL}/api/emergencycontact/emergency-contact?userId=${userId}`,
        form,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      alert("Emergency contact saved!");
    } catch (err) {
      console.error("Submit error:", err?.response?.data || err?.message);
      alert("Failed to save emergency contact.");
    }
  };

  const renderInput = (name, label, type = "text") => (
    <div className="col-md-4 mb-3">
      <label>{label}</label>
      <input
        type={type}
        name={name}
        className="form-control"
        value={form[name] || ""}
        onChange={handleChange}
        disabled={isReadOnly && !isAdmin} // admins can edit anytime
      />
    </div>
  );

  return (
    <>
      <div className="d-flex justify-content-between align-items-center pt-3">
        <div className="d-flex align-items-center">
          <span className="me-2 text-danger"></span>
          <span style={{ fontSize: "18px", fontWeight: "500" }}>
            Emergency Contact Information
          </span>
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(-1)}
        >
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Emergency Contact Information</h2>
          </div>
          <CompanyAddress />
        </div>

        <form onSubmit={handleSubmit}>
          <fieldset className="border p-3 mb-4">
            <legend className="w-auto px-2 mb-4">Emergency Contact #1</legend>
            <div className="row">
              {renderInput("ec1_name", "Name")}
              {renderInput("ec1_relationship", "Relationship")}
              {renderInput("ec1_phone", "Phone", "tel")}
            </div>
            {renderInput("ec1_address", "Address")}
            {renderInput("ec1_email", "Email", "email")}
          </fieldset>

          <fieldset className="border p-3 mb-4">
            <legend className="w-auto px-2 mb-4">Emergency Contact #2</legend>
            <div className="row">
              {renderInput("ec2_name", "Name")}
              {renderInput("ec2_relationship", "Relationship")}
              {renderInput("ec2_phone", "Phone", "tel")}
            </div>
            {renderInput("ec2_address", "Address")}
            {renderInput("ec2_email", "Email", "email")}
          </fieldset>

          <div className="d-flex justify-content-end">
            {(!isSigned || isAdmin) && (
              <button type="submit" className="btn btn-primary px-5">
                Submit
              </button>
            )}
          </div>
        </form>

        <hr className="my-5" />

        {userId && (
          <>
            <SignatureManager
              taskName="emergency_contact"
              taskId={3}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
            <ReviewManager
              taskName="emergency_contact"
              taskId={3}
              userId={isAdmin && paramId ? paramId : userId}
              isAdmin={isAdmin}
            />
          </>
        )}
      </div>
    </>
  );
}

export default EmergencyContact;
